<script setup lang="ts">
import ConfirmDlg from '@/components/confirm-dlg.vue'
import KpTextfield from '@/components/kp-textfield.vue'
import KpToolbar from '@/components/kp-toolbar.vue'
import { useToast } from '@/composables/toast'
import { instance as api } from '@/helpers/api'
import { useAuthUser } from '@/helpers/globals'
import { errToStr } from '@/helpers/tools'
import { Team } from '@/models/Team'
import { type TeamMitglied, TeamMitgliedCollection } from '@/models/TeamMitglied'
import type { Ref } from 'vue'
import { ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { registerFormRouteGuard } from '@/composables/routerGuard'
import { formRules } from '@/helpers/validators'
import type { DataTableHeader } from 'vuetify'

registerFormRouteGuard(isDirty)

const loading = ref(false)
const initialLoading = ref(false)
const teamLoading = ref(false)
const memberAddDlgLoading = ref(false)
const memberRemoveDlgLoading = ref(false)
const leaveTeamDlgLoading = ref(false)

// mixins: [routeGuardFormConfirmMixin],
const confirmDelDlg = ref(false)
const team: Ref<Team | null> = ref(null)
const valid = ref(false)
const { t } = useI18n()
const toast = useToast()

const members = new TeamMitgliedCollection()
const teamHeaders: DataTableHeader[] = [
    { title: t('name'), value: 'team_mitglied_name', sortable: true },
    { title: t('email'), value: 'benutzer_email', sortable: true },
    { title: t('status'), value: 'status_display', sortable: true },
    {
        value: 'action',
        align: 'end',
        width: '120px',
        sortable: false,
    },
]
const memberAddDlg = ref(false)
const memberEmail: Ref<string | null> = ref(null)
const newMemberFormValid = ref(false)
const confirmRemoveMemberDlg = ref(false)
const memberToRemove: Ref<TeamMitglied | null> = ref(null)
const conifirmLeaveTeamDlg = ref(false)

const rules = formRules

const benutzer = useAuthUser()
const route = useRoute()
const router = useRouter()

// watch(route, () => {
//     loadRecord(route.params.id as string)
// })

watch(team, (newVal, oldVal) => {
    if (newVal && (!oldVal || oldVal.id !== newVal.id)) {
        loadTeamMembers(newVal.id)
    }
})

onMounted(() => {
    loadRecord(route.params.id as string)
})

async function loadRecord(id: string) {
    try {
        const numId = Number.parseInt(id, 10)
        if (numId > 0) {
            initialLoading.value = true
            team.value = await Team.loadById(numId)
        }
    } catch (res) {
        toast.add({ text: errToStr(res), color: 'error' })
    } finally {
        initialLoading.value = false
    }
}

async function loadTeamMembers(teamId: number | null) {
    try {
        teamLoading.value = true
        await members.query({ team_id: teamId })
    } catch (res) {
        toast.add({ text: errToStr(res), color: 'error' })
    } finally {
        teamLoading.value = false
    }
}

async function onSubmit() {
    try {
        await team.value?.save()
        closeForm()
    } catch (res) {
        toast.add({ text: errToStr(res), color: 'error' })
    }
}

async function onDestroy() {
    confirmDelDlg.value = true
}

function closeForm() {
    router.go(-1)
}

async function confirmDestroy() {
    try {
        await team.value?.destroy()
        team.value = null
        closeForm()
    } catch (e) {
        toast.add({ text: errToStr(e), color: 'error' })
    }
}

function isDirty() {
    return team.value ? team.value.isDirty() : false
}

function onMemberAddBtn() {
    memberAddDlg.value = true
    memberEmail.value = ''
    newMemberFormValid.value = false
}

async function onNewMember() {
    const email = (memberEmail.value || '').trim()
    if (email.length < 1) {
        return
    }
    memberAddDlgLoading.value = true
    try {
        await addMemberRequest(team.value, memberEmail.value)
        memberAddDlg.value = false
        toast.add({ text: t('invitation_to_sent', { email: memberEmail.value }) })
        loadTeamMembers(team.value?.id || null)
    } catch (e) {
        toast.add({ text: errToStr(e), color: 'error' })
    } finally {
        memberAddDlgLoading.value = false
    }
}

async function addMemberRequest(team: Team | null, email: string | null) {
    return await api().post('/team/addMember', {
        team_id: team?.id,
        email: email,
    })
}

function onRemoveMember(member: TeamMitglied) {
    memberToRemove.value = member
    confirmRemoveMemberDlg.value = true
}

async function doRemoveMember() {
    if (!memberToRemove.value) {
        return
    }

    try {
        memberRemoveDlgLoading.value = true
        const userId = memberToRemove.value.benutzer_id
        await memberToRemove.value.destroy()
        confirmRemoveMemberDlg.value = false
        if (userId === benutzer?.id) {
            closeForm()
        } else {
            loadTeamMembers(team.value?.id || null)
        }
    } catch (e) {
        toast.add({ text: errToStr(e), color: 'error' })
    } finally {
        memberRemoveDlgLoading.value = false
    }
}

function confirmLeaveTeam() {
    conifirmLeaveTeamDlg.value = true
}

async function leaveTeam() {
    leaveTeamDlgLoading.value = true
    try {
        const memberToRemove = members.find((m) => m.benutzer_id === benutzer?.id)
        await memberToRemove?.destroy()
        closeForm()
    } catch (e) {
        toast.add({ text: errToStr(e), color: 'error' })
    } finally {
        leaveTeamDlgLoading.value = false
    }
}
</script>

<template>
    <v-skeleton-loader
        v-if="initialLoading"
        class="centered-card"
        type="list-item-avatar, list-item-three-line@3, actions"
    ></v-skeleton-loader>
    <div v-else>
        <KpToolbar icon="fas fa-users" :title="team?.name || ''"> </KpToolbar>
        <v-container fluid>
            <v-card
                class="centered-card pl-4"
                v-if="team?.ersteller_id === benutzer?.id"
                max-width="950"
            >
                <v-form v-model="valid" autocomplete="new-password">
                    <v-card-text v-if="team">
                        <v-row>
                            <v-col cols="12">
                                <kp-textfield
                                    v-model="team.name"
                                    :label="t('team_name')"
                                    :rules="[rules.required, rules.maxLength(200)]"
                                ></kp-textfield>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn
                            class="hidden-sm-and-up"
                            color="red"
                            :disabled="loading"
                            text
                            @click="onDestroy"
                        >
                            <v-icon>fas fa-trash</v-icon>
                        </v-btn>
                        <v-btn
                            class="hidden-xs"
                            color="red"
                            :disabled="loading"
                            text
                            @click="onDestroy"
                        >
                            {{ t('delete') }}
                        </v-btn>

                        <v-spacer />
                        <v-btn
                            class="hidden-sm-and-up"
                            color="decline"
                            text
                            :disabled="loading"
                            @click="closeForm"
                        >
                            <v-icon>fas fa-arrow-left</v-icon>
                        </v-btn>
                        <v-btn
                            class="hidden-xs"
                            color="decline"
                            :disabled="loading"
                            text
                            @click="closeForm"
                        >
                            {{ t('close') }}
                        </v-btn>
                        <v-btn
                            class="hidden-xs"
                            color="action white--text"
                            variant="flat"
                            :disabled="!valid || loading"
                            :loading="loading"
                            @click="onSubmit"
                            prepend-icon="fa-solid fa-floppy-disk"
                        >
                            {{ t('save') }}
                        </v-btn>
                        <v-btn
                            class="hidden-sm-and-up"
                            color="action white--text"
                            variant="flat"
                            :disabled="!valid || loading"
                            :loading="loading"
                            @click="onSubmit"
                            icon="fa-solid fa-floppy-disk"
                        >
                        </v-btn>
                    </v-card-actions>
                    <ConfirmDlg
                        v-model="confirmDelDlg"
                        :title="t('bestaetigung')"
                        :text="t('really_delete_rec')"
                        @yes="confirmDestroy"
                    />
                </v-form>
            </v-card>
            <v-card class="centered-card" v-else max-width="950">
                <v-card-actions>
                    <v-btn
                        class="hidden-sm-and-up"
                        color="error darken-1"
                        text
                        :disabled="loading"
                        @click="confirmLeaveTeam"
                    >
                        <v-icon>fas fa-sign-out-alt</v-icon>
                    </v-btn>
                    <v-btn
                        class="hidden-xs"
                        color="error darken-1"
                        :disabled="loading"
                        text
                        @click="confirmLeaveTeam"
                    >
                        {{ t('leave_team') }}
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        class="hidden-sm-and-up"
                        color="decline"
                        text
                        :disabled="loading"
                        @click="closeForm"
                    >
                        <v-icon>fas fa-arrow-left</v-icon>
                    </v-btn>
                    <v-btn
                        class="hidden-xs"
                        color="decline"
                        :disabled="loading"
                        text
                        @click="closeForm"
                    >
                        {{ t('close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>

            <v-card
                class="centered-card mt-5"
                v-if="!initialLoading"
                color="grey-lighten-3"
                max-width="950"
            >
                <v-card-title>
                    <div class="header-container">
                        <div>
                            <v-icon size="small">fas fa-user</v-icon>
                            <span class="ml-4">{{ t('members') }}</span>
                        </div>
                        <v-btn
                            v-if="team?.ersteller_id === benutzer?.id"
                            rounded="xl"
                            class="hidden-sm-and-up"
                            color="action white--text"
                            @click="onMemberAddBtn"
                            icon="fas fa-plus"
                        >
                        </v-btn>
                        <v-btn
                            v-if="team?.ersteller_id === benutzer?.id"
                            rounded="xl"
                            class="hidden-xs"
                            color="action white--text"
                            @click="onMemberAddBtn"
                            prepend-icon="fas fa-plus"
                        >
                            {{ t('member_add') }}
                        </v-btn>
                    </div>
                </v-card-title>
                <v-card-text>
                    <v-data-table-server
                        class="border-thin"
                        :headers="teamHeaders"
                        :items-length="members.getServerTotal()"
                        :items="members ? members.getModels() : []"
                        item-key="id"
                        :loading="teamLoading"
                        :no-data-text="t('no_records')"
                        disable-pagination
                        hide-default-footer
                    >
                        <template v-slot:[`item.team_mitglied_name`]="{ item }">
                            <strong v-if="item.team_ersteller_id === item.benutzer_id">{{
                                item.team_mitglied_name
                            }}</strong>
                            <template v-else>{{ item.team_mitglied_name }}</template>
                        </template>

                        <template v-slot:[`item.benutzer_email`]="{ item }">
                            <strong v-if="item.team_ersteller_id === item.benutzer_id">{{
                                item.benutzer_email
                            }}</strong>
                            <template v-else>{{ item.benutzer_email }}</template>
                        </template>

                        <template v-slot:[`item.action`]="{ item }">
                            <v-btn
                                v-if="
                                    item.team_ersteller_id === benutzer?.id &&
                                    item.benutzer_id !== benutzer?.id
                                "
                                x-small
                                fab
                                rounded
                                color="error"
                                @click="onRemoveMember(item)"
                            >
                                <v-icon x-small>fas fa-trash</v-icon>
                            </v-btn>
                        </template>
                        <template v-slot:[`item.status_display`]="{ item }">
                            <v-tooltip top>
                                <template
                                    v-if="item.team_ersteller_id === item.benutzer_id"
                                    v-slot:activator="{ props }"
                                >
                                    <v-icon color="primary" v-bind="props" size="x-small"
                                        >fas fa-user</v-icon
                                    >
                                </template>
                                <template v-else v-slot:activator="{ props }">
                                    <v-icon
                                        :color="item.status_display.color"
                                        v-bind="props"
                                        x-small
                                    >
                                        {{ item.status_display.icon }}
                                    </v-icon>
                                </template>
                                <span v-if="item.team_ersteller_id === item.benutzer_id">{{
                                    t('admin')
                                }}</span>
                                <span v-else>{{ item.status_display.text }}</span>
                            </v-tooltip>
                        </template>
                    </v-data-table-server>
                </v-card-text>
            </v-card>

            <!-- Member add dlg -->
            <ConfirmDlg
                v-model="memberAddDlg"
                :title="t('member_add')"
                :yes-text="t('invite')"
                :no-text="t('cancel')"
                :close-on-yes="false"
                :loading="memberAddDlgLoading"
                max-width="400"
                :disable-yes="!newMemberFormValid"
                @yes="onNewMember"
            >
                <v-form v-model="newMemberFormValid" style="padding: 1rem 1rem 1rem 2rem">
                    <p>
                        {{ t('add_member_msg') }}
                    </p>
                    <p>
                        {{ t('add_member_warning') }}
                    </p>
                    <kp-textfield
                        v-model:model-value="memberEmail"
                        type="email"
                        :rules="[rules.required, rules.email]"
                        :placeholder="t('emailaddress')"
                        autofocus
                    />
                </v-form>
            </ConfirmDlg>
            <!-- remove member confirm dialog -->
            <ConfirmDlg
                v-model="confirmRemoveMemberDlg"
                :title="t('bestaetigung')"
                :text="t('really_delete_rec')"
                :loading="memberRemoveDlgLoading"
                @yes="doRemoveMember"
                @no="memberToRemove = null"
            />
            <!-- leave team confirm dialog -->
            <ConfirmDlg
                v-model="conifirmLeaveTeamDlg"
                :title="t('leave_team')"
                :text="t('leave_team_txt')"
                :loading="leaveTeamDlgLoading"
                @yes="leaveTeam"
            />
        </v-container>
    </div>
</template>

<style lang="css" scoped>
.header-container {
    margin-top: 1rem;
    display: flex;
    justify-content: space-between;
}
</style>
